import {FC, ReactNode, useCallback, useState, useEffect, useRef} from 'react'
import {SelectProvider, useComboboxStore} from '@ariakit/react'
import {
    SelectComboboxInput,
    SelectItem,
    SelectPopover,
    SelectTrigger
} from '@components/ui/select-atoms/SelectAtoms.tsx'
import {Checkbox} from '@components/commons/checkbox/ChekBox.tsx'
import {
    StyledDisplayValueWrapper,
    StyledSelectListWrapper,
    StyledSelectNoResult,
    StyledCountIndicator
} from '@components/ui/multi-select/style.ts'
import {useTranslation} from 'react-i18next'
import {debounce} from '@utilities/helpers.ts'
import {Virtuoso} from 'react-virtuoso'
import {Flexbox} from '../flexbox/FlexBox'
import {Spinner} from '../spinner/Spinner'

export interface MultiSelectProps {
    options: {id: string; name: string}[]
    displayLabel: string
    displayIcon: ReactNode
    searchPlaceholder: string
    value: {id: string; name: string}[]
    onChange: (value: {id: string; name: string}[]) => void
    sameWidth?: boolean
    hasNextPage?: boolean
    isFetchingNextPage?: boolean
    fetchNextPage?: () => void
    setSearchSections?: (value: string) => void
    isLoading?: boolean
}

export const MultiSelect: FC<MultiSelectProps> = ({
    value,
    onChange,
    options,
    displayLabel,
    displayIcon,
    searchPlaceholder,
    sameWidth = false,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    setSearchSections,
    isLoading = false
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [filteredOptions, setFilteredOptions] = useState(options)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const comboboxStore = useComboboxStore()

    const debouncedSearch = useCallback(
        debounce((query: string) => {
            setSearchSections?.(query)
        }, 500),
        [setSearchSections]
    )

    useEffect(() => {
        if (!options) return
        setFilteredOptions(
            searchQuery
                ? options.filter(option => option.name.toLowerCase().includes(searchQuery.toLowerCase()))
                : options
        )
    }, [options, searchQuery])

    useEffect(() => {
        setFilteredOptions(options || [])
    }, [options])

    useEffect(() => {
        debouncedSearch(searchQuery)
    }, [searchQuery, debouncedSearch])

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                comboboxStore.setOpen(true)
                inputRef.current?.focus()
            }, 50)
        }
    }, [isOpen, comboboxStore])

    useEffect(() => {
        if (isOpen && searchQuery) {
            inputRef.current?.focus()
        }
    }, [options, isOpen, searchQuery])

    const handleClose = () => {
        setIsOpen(false)
        setSearchQuery('')
        setSearchSections?.('')
        setFilteredOptions(options)
    }

    const handleChange = (item: {id: string; name: string}) => {
        const isSelected = value.some(v => v.id === item.id)
        const newValue = isSelected ? value.filter(v => v.id !== item.id) : [...value, item]
        onChange(newValue)
    }

    return (
        <SelectProvider value={value.map(v => v.id)}>
            <SelectTrigger
                displayValue={() => (
                    <StyledDisplayValueWrapper gap={2} align={'center'}>
                        <p>
                            {displayLabel}
                            <StyledCountIndicator className={value.length > 0 ? 'visible' : ''}>
                                {value.length > 0 && ` (${value.length})`}
                            </StyledCountIndicator>
                        </p>
                        {displayIcon}
                    </StyledDisplayValueWrapper>
                )}
                onClick={() => setIsOpen(prev => !prev)}
            />
            <SelectPopover gutter={8} sameWidth={sameWidth} unmountOnHide onClose={handleClose}>
                <SelectComboboxInput
                    ref={inputRef}
                    store={comboboxStore}
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    autoFocus
                    tabIndex={-1}
                    onChange={event => setSearchQuery(event.target.value)}
                />
                <StyledSelectListWrapper>
                    {isLoading || isFetchingNextPage ? (
                        <Flexbox justify={'center'} align={'center'} gap={4} className="spinner-container">
                            <Spinner />
                        </Flexbox>
                    ) : (
                        filteredOptions?.length === 0 &&
                        searchQuery.length > 0 && (
                            <StyledSelectNoResult>{t('commons:no_results_found_for')}</StyledSelectNoResult>
                        )
                    )}

                    <Virtuoso
                        data={filteredOptions || []}
                        itemContent={(_, item) => (
                            <>
                                <SelectItem
                                    key={item.id}
                                    value={item.id}
                                    onClick={() => handleChange(item)}
                                    setValueOnClick
                                >
                                    <Checkbox
                                        id={item.id}
                                        checked={value.some(v => v.id === item.id)}
                                        onChange={() => handleChange(item)}
                                    />
                                    <span>{item.name}</span>
                                </SelectItem>
                            </>
                        )}
                        style={{height: '200px', overflowX: 'hidden'}}
                        endReached={() => {
                            if (hasNextPage && !isFetchingNextPage) {
                                fetchNextPage?.()
                            }
                        }}
                    />
                </StyledSelectListWrapper>
            </SelectPopover>
        </SelectProvider>
    )
}
